import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

// Import příležitostí: Excel se sloupcem A = IČO a sloupcem B = datum příležitosti.
// Podle IČO se najdou firmy a nastaví se jim pole "Příležitost" (opportunityDate).
function ImportOpportunities({ onClose, isDarkMode, currentUserId }) {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [results, setResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Převod Excel hodnoty (serial number, Date, nebo text) na "YYYY-MM-DD".
  // Speciální textové hodnoty (např. "Neověřeno") se vrací tak, jak jsou.
  const parseOpportunityValue = (value) => {
    if (value == null || value === '') return '';

    // XLSX s cellDates:true vrací Date objekty
    if (value instanceof Date && !isNaN(value.getTime())) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    // Excel serial number (číslo dní od 1899-12-30)
    if (typeof value === 'number' && isFinite(value)) {
      const epoch = new Date(Date.UTC(1899, 11, 30));
      const dt = new Date(epoch.getTime() + Math.round(value) * 86400000);
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    const str = String(value).trim();
    if (!str) return '';

    // Už ve formátu YYYY-MM-DD
    const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2].padStart(2, '0')}-${isoMatch[3].padStart(2, '0')}`;
    }

    // Český formát D.M.YYYY nebo D. M. YYYY
    const czMatch = str.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{2,4})$/);
    if (czMatch) {
      let year = czMatch[3];
      if (year.length === 2) year = `20${year}`;
      return `${year}-${czMatch[2].padStart(2, '0')}-${czMatch[1].padStart(2, '0')}`;
    }

    // Formát D/M/YYYY
    const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      let year = slashMatch[3];
      if (year.length === 2) year = `20${year}`;
      return `${year}-${slashMatch[2].padStart(2, '0')}-${slashMatch[1].padStart(2, '0')}`;
    }

    // Pokud jde o čistě číselný/datumový text, zkus poslední parsování jako datum
    const parsed = new Date(str);
    if (/\d/.test(str) && !isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    // Jinak jde o textovou hodnotu (např. "Neověřeno") – ulož text tak, jak je
    return str;
  };

  const readExcelRows = (fileToRead) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });

          const rows = [];
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const rawIco = String(row[0] == null ? '' : row[0]).trim();
            const ico = rawIco.replace(/[^\d]/g, ''); // normalizace IČO – jen číslice
            const rawDate = row[1];
            const opportunityDate = parseOpportunityValue(rawDate);

            // Přeskoč prázdné řádky
            if (!ico && !opportunityDate) continue;

            rows.push({ ico, rawIco, rawDate: String(rawDate == null ? '' : rawDate), opportunityDate });
          }
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Nepodařilo se přečíst soubor'));
      reader.readAsArrayBuffer(fileToRead);
    });

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
      setMessage('');
      setPreviewData(null);
      setShowPreview(false);
    }
  };

  const generatePreview = async () => {
    if (!file) {
      alert('Vyberte prosím Excel soubor');
      return;
    }

    setIsProcessing(true);
    setProgress(10);
    setMessage('Načítám Excel soubor...');

    try {
      const rows = await readExcelRows(file);

      // Odfiltruj řádek s hlavičkou (první buňka není číselné IČO → jde o hlavičku, např. "IČO")
      let dataRows = rows;
      if (rows.length > 0 && rows[0].rawIco && isNaN(Number(rows[0].rawIco))) {
        dataRows = rows.slice(1);
      }

      const validRows = dataRows.filter(r => r.ico && r.opportunityDate);
      if (validRows.length === 0) {
        throw new Error('Nenašly se žádné platné řádky (sloupec A = IČO, sloupec B = datum nebo text, např. "Neověřeno").');
      }

      setProgress(50);
      setMessage('Hledám firmy v databázi podle IČO...');

      const uniqueIcos = [...new Set(validRows.map(r => r.ico))];
      const found = [];
      const batchSize = 500;
      for (let i = 0; i < uniqueIcos.length; i += batchSize) {
        const batch = uniqueIcos.slice(i, i + batchSize);
        const response = await fetch(`${API_URL}/companies/by-ico?userId=${currentUserId || ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icos: batch, userId: currentUserId }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Chyba při načítání firem: ${response.status} - ${errorText}`);
        }
        const batchCompanies = await response.json();
        found.push(...batchCompanies);
      }

      // Mapa IČO -> firma
      const companyByIco = new Map();
      found.forEach(c => {
        const key = String(c.ico == null ? '' : c.ico).replace(/[^\d]/g, '');
        if (!companyByIco.has(key)) companyByIco.set(key, []);
        companyByIco.get(key).push(c);
      });

      const preview = [];
      let matchCount = 0;
      let notFoundCount = 0;
      validRows.forEach(r => {
        const matches = companyByIco.get(r.ico);
        if (matches && matches.length > 0) {
          matches.forEach(company => {
            matchCount++;
            preview.push({
              ico: r.ico,
              name: company.name,
              oldDate: company.opportunityDate || '(prázdné)',
              newDate: r.opportunityDate,
              found: true,
            });
          });
        } else {
          notFoundCount++;
          preview.push({
            ico: r.ico,
            name: '(firma nenalezena)',
            oldDate: '',
            newDate: r.opportunityDate,
            found: false,
          });
        }
      });

      setPreviewData({
        totalRows: validRows.length,
        matchedCompanies: matchCount,
        notFound: notFoundCount,
        preview: preview.slice(0, 100),
        updates: validRows.map(r => ({ ico: r.ico, opportunityDate: r.opportunityDate })),
      });

      setProgress(100);
      setMessage(`✅ Náhled připraven! Nalezeno ${matchCount} firem, ${notFoundCount} bez shody.`);
      setShowPreview(true);
      setIsProcessing(false);
    } catch (error) {
      console.error('❌ Chyba při generování náhledu:', error);
      setMessage(`❌ Chyba: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const confirmImport = async () => {
    if (!previewData || !previewData.updates || previewData.updates.length === 0) return;

    setIsProcessing(true);
    setProgress(30);
    setMessage(`Importuji ${previewData.updates.length} příležitostí...`);

    try {
      const response = await fetch(`${API_URL}/companies/bulk-opportunity-update?userId=${currentUserId || ''}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: previewData.updates, userId: currentUserId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chyba serveru: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      setProgress(100);
      setMessage('✅ Import dokončen!');
      setResults(result);
      setShowPreview(false);
      setIsProcessing(false);
    } catch (error) {
      console.error('❌ Chyba při importu:', error);
      setMessage(`❌ Chyba: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const formatDisplayDate = (isoDate) => {
    if (!isoDate || isoDate === '(prázdné)') return isoDate || '';
    const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return isoDate;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6`}>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">📅 Import příležitostí</h2>
          <button
            onClick={onClose}
            className={`${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ✕
          </button>
        </div>

        {/* Instrukce */}
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded p-4 mb-6`}>
          <h3 className="font-semibold mb-2">📋 Požadovaný formát Excel souboru:</h3>
          <ul className="text-sm space-y-1">
            <li><strong>Sloupec A:</strong> IČO firmy</li>
            <li><strong>Sloupec B:</strong> Datum příležitosti (např. 1.8.2026 nebo 2026-08-01) <em>nebo</em> text „Neověřeno"</li>
          </ul>
          <p className="text-xs mt-2 opacity-75">
            💡 První řádek může obsahovat hlavičky – budou automaticky přeskočeny. Firmy se spárují podle IČO a nastaví se jim hodnota do pole „Příležitost" (datum nebo text „Neověřeno").
          </p>
        </div>

        {/* File upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Vyberte Excel soubor:</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className={`w-full p-2 border rounded-lg ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
            disabled={isProcessing}
          />
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>{message}</span>
              <span>{progress}%</span>
            </div>
            <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
              <div
                className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Zpráva (mimo processing) */}
        {!isProcessing && message && !showPreview && !results && (
          <div className={`mb-6 text-sm ${message.startsWith('❌') ? 'text-red-500' : ''}`}>{message}</div>
        )}

        {/* Preview */}
        {showPreview && previewData && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded p-4 mb-6`}>
            <h3 className="font-semibold text-blue-600 mb-4">👀 Náhled změn</h3>
            <div className="text-sm mb-4">
              <p><strong>Celkem řádků v Excelu:</strong> {previewData.totalRows}</p>
              <p><strong>Nalezeno firem (podle IČO):</strong> {previewData.matchedCompanies}</p>
              <p><strong>Bez shody:</strong> {previewData.notFound}</p>
              <p className="text-xs opacity-75">Zobrazeno prvních 100 záznamů</p>
            </div>

            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <th className="border p-2">IČO</th>
                    <th className="border p-2">Název firmy</th>
                    <th className="border p-2">Příležitost (změna)</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.preview.map((item, index) => (
                    <tr
                      key={index}
                      className={
                        !item.found
                          ? (isDarkMode ? 'bg-red-900/40' : 'bg-red-50')
                          : index % 2 === 0
                            ? (isDarkMode ? 'bg-gray-800' : 'bg-white')
                            : (isDarkMode ? 'bg-gray-700' : 'bg-gray-50')
                      }
                    >
                      <td className="border p-2">{item.ico}</td>
                      <td className="border p-2">{item.name}</td>
                      <td className="border p-2">
                        {item.found ? (
                          <span>
                            <span className="line-through opacity-70">{formatDisplayDate(item.oldDate)}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold text-green-600">{formatDisplayDate(item.newDate)}</span>
                          </span>
                        ) : (
                          <span className="text-red-500">nenalezeno</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Výsledky */}
        {results && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded p-4 mb-6`}>
            <h3 className="font-semibold text-green-600 mb-2">✅ Import dokončen</h3>
            <p className="text-sm"><strong>Aktualizováno firem:</strong> {results.updatedCount}</p>
            <p className="text-sm"><strong>Nenalezeno (IČO bez shody):</strong> {results.notFoundCount}</p>
            {results.errors && results.errors.length > 0 && (
              <div className="mt-2 text-xs text-red-500">
                <p className="font-semibold">Chyby:</p>
                <ul className="list-disc ml-4">
                  {results.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Akční tlačítka */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            {results ? 'Zavřít' : 'Zrušit'}
          </button>
          {!showPreview && !results && (
            <button
              onClick={generatePreview}
              disabled={!file || isProcessing}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition disabled:opacity-60"
            >
              👀 Vytvořit náhled
            </button>
          )}
          {showPreview && !results && (
            <button
              onClick={confirmImport}
              disabled={isProcessing}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition disabled:opacity-60"
            >
              ✅ Potvrdit import
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportOpportunities;
